"use client";

import { Leaf } from "lucide-react";
import React from "react";
import { useTranslations } from "next-intl";
import { twMerge } from "tailwind-merge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const ENERGY_CHIP_STYLES: Record<string, string> = {
  A: "bg-green-100 text-green-700 border-green-200",
  B: "bg-green-100 text-green-700 border-green-200",
  C: "bg-amber-100 text-amber-700 border-amber-200",
  D: "bg-amber-100 text-amber-700 border-amber-200",
  E: "bg-red-100 text-red-700 border-red-200",
  F: "bg-red-100 text-red-700 border-red-200",
  G: "bg-red-100 text-red-700 border-red-200",
};

type Props = {
  label: string;
  className?: string;
};

/** Colored energy-label chip with an explanatory tooltip. */
const EnergyChip: React.FC<Props> = ({ label, className }) => {
  const t = useTranslations("listing");
  const key = label.charAt(0).toUpperCase();

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        {/* asChild avoids a <button> inside the card's <a>, which breaks both
            HTML validity and the tooltip's anchor positioning. */}
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            role="note"
            aria-label={t("energy-label-text", { label })}
            onClick={(e) => e.preventDefault()}
            className={twMerge(
              "inline-flex flex-none cursor-help items-center gap-0.5 rounded-md border px-1.5 py-px text-[10px] font-bold",
              ENERGY_CHIP_STYLES[key] ??
                "border-border bg-muted text-muted-foreground",
              className,
            )}
          >
            <Leaf className="size-2.5" strokeWidth={2.5} />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={6} className="max-w-60">
          <p className="text-xs leading-relaxed">
            {t("energy-tooltip", { label })}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default EnergyChip;
